/**
 * /community/tips — FitTips Library.
 * Tabs: All / Workout / Nutrition / Recovery / Form / Mindset.
 * Search bar + saved-tips list.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { ArrowLeft, Search, Bookmark } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api";
import FitTipCard from "../../components/community/FitTipCard";

const CATS = [
  { v: "all", l: "All" },
  { v: "workout", l: "Workout" },
  { v: "nutrition", l: "Nutrition" },
  { v: "recovery", l: "Recovery" },
  { v: "form", l: "Form" },
  { v: "mindset", l: "Mindset" },
];

export default function FitTipsLibrary() {
  const navigate = useNavigate();
  const [tips, setTips] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [showSavedOnly, setShowSavedOnly] = useState(false);

  const load = useCallback(() => {
    apiClient
      .get("/fittips", { params: { category: cat, saved_only: showSavedOnly } })
      .then((r) => setTips(r.data || []));
  }, [cat, showSavedOnly]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return tips;
    const needle = q.toLowerCase();
    return tips.filter(
      (t) => t.tip_text.toLowerCase().includes(needle) || (t.trainer_name || "").toLowerCase().includes(needle),
    );
  }, [tips, q]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-base, #ECEAE6)" }} data-testid="fittips-library">
      <div
        style={{
          position: "sticky", top: 0, zIndex: 10,
          background: "var(--bg-elevated, #F0EEEA)",
          borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))",
          padding: "16px 16px 12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => navigate(-1)} aria-label="Back" style={{ background: "transparent", border: "none" }}>
            <ArrowLeft size={20} />
          </button>
          <h1 style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: 24, fontWeight: 800, margin: 0 }}>
            FitTips Library
          </h1>
        </div>
        <div style={{ position: "relative", marginTop: 12 }}>
          <Search size={14} style={{ position: "absolute", top: 13, left: 14, color: "var(--text-tertiary, #8A8884)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tips, trainers…"
            data-testid="fittips-search"
            style={{
              width: "100%", height: 40, paddingLeft: 38, paddingRight: 14,
              background: "var(--bg-inset, #D8D6D1)", border: "none",
              borderRadius: 12, fontFamily: "Barlow", fontSize: 14,
              color: "var(--text-primary, #0C0C0B)", outline: "none",
            }}
          />
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 12, overflowX: "auto", paddingBottom: 4 }}>
          {CATS.map((c) => (
            <button
              key={c.v}
              onClick={() => setCat(c.v)}
              data-testid={`fittip-cat-${c.v}`}
              style={{
                flex: "0 0 auto",
                padding: "6px 12px",
                background: cat === c.v ? "#0C0C0B" : "transparent",
                color: cat === c.v ? "white" : "var(--text-secondary, #4A4845)",
                border: "1px solid",
                borderColor: cat === c.v ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))",
                borderRadius: 100,
                fontFamily: "Barlow", fontSize: 12, fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {c.l}
            </button>
          ))}
          <button
            onClick={() => setShowSavedOnly((s) => !s)}
            data-testid="fittip-saved-toggle"
            style={{
              flex: "0 0 auto", padding: "6px 12px",
              background: showSavedOnly ? "#F5C518" : "transparent",
              color: "#0C0C0B",
              border: "1px solid",
              borderColor: showSavedOnly ? "#F5C518" : "var(--border, rgba(12,12,11,0.16))",
              borderRadius: 100, fontFamily: "Barlow", fontSize: 12, fontWeight: 700,
              whiteSpace: "nowrap",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
          >
            <Bookmark size={11} /> Saved
          </button>
        </div>
      </div>

      <div style={{ padding: 16, maxWidth: 720, margin: "0 auto", display: "grid", gap: 12 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>
              {showSavedOnly ? "No saved tips yet" : "No tips here"}
            </div>
            <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)", marginTop: 6 }}>
              {showSavedOnly ? "Save a tip from the feed to see it here." : "Check back soon."}
            </p>
          </div>
        ) : (
          filtered.map((t) => <FitTipCard key={t.tip_id} tip={t} onChange={load} />)
        )}
      </div>
    </div>
  );
}
